import React, { useRef, useState } from "react";
import { uploadToDropbox } from "../lib/dropboxUpload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Upload, FileVideo, FileAudio, X } from "lucide-react";

// Per-mode config — keeps the dialog narrow and avoids a second copy of
// the dropzone/progress-bar UI just to upload audio.
const MODES = {
  diretto: {
    title: "Carica video diretto",
    icon: Upload,
    fileIcon: FileVideo,
    accept: "video/mp4,video/quicktime,video/webm,video/*",
    fileLabel: "Trascina qui il video o clicca per sceglierlo",
    typeHint: "Video",
    maxMB: 500,
    folderHint: "/Clienti/<Salone>/Diretti/",
    description:
      "Salta la fase Lavorato. Il file va su Dropbox e si apre subito Prepara con AI.",
    uploadKind: "diretto",
    successToast:
      "Video caricato — apri il dialog Prepara per la caption AI",
    submitLabel: "Carica e prepara",
  },
  musica: {
    title: "Carica musica",
    icon: FileAudio,
    fileIcon: FileAudio,
    accept: "audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg,audio/*",
    fileLabel: "Trascina qui la traccia o clicca per sceglierla",
    typeHint: "Audio",
    maxMB: 50,
    folderHint: "/Clienti/<Salone>/Musica/",
    description:
      "Salva la traccia nella cartella Musica del cliente su Dropbox.",
    uploadKind: "musica",
    successToast: "Traccia caricata in Dropbox",
    submitLabel: "Carica",
  },
};

function prettyBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * DirectUploadDialog
 * Browser → Dropbox direct chunked upload (no Cloud Run proxy).
 * Mode "diretto": video in /Diretti/YYYY-MM/DD/ + creates synthetic message
 *                 → triggers Prepara con AI.
 * Mode "musica":  audio in /Musica/ (no DB record, just files).
 */
export default function DirectUploadDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  onUploaded,
  mode = "diretto",
}) {
  const cfg = MODES[mode] || MODES.diretto;
  const MAX_MB = cfg.maxMB;
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setProgress(0);
      setUploading(false);
    }
  }, [open]);

  const pick = () => inputRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande (max ${MAX_MB}MB)`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande (max ${MAX_MB}MB)`);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file || !clientId) return;
    setUploading(true);
    setProgress(0);
    try {
      const data = await uploadToDropbox({
        kind: cfg.uploadKind,
        file,
        clientId,
        onProgress: (p) => setProgress(p),
      });
      toast.success(cfg.successToast, { duration: 6000 });
      onUploaded && onUploaded(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`direct-upload-dialog-${mode}`}
        className="max-w-lg rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <cfg.icon className="h-5 w-5" /> {cfg.title}
          </DialogTitle>
          <DialogDescription>
            {clientName ? `${clientName} · ` : ""}
            {cfg.description}{" "}
            <code className="mx-1 px-1 rounded bg-[var(--mp-dash-bg)]">
              {cfg.folderHint}
            </code>
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={pick}
          data-testid={`direct-dropzone-${mode}`}
          className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            file
              ? "border-[var(--mp-ink)] bg-[var(--mp-dash-bg)]"
              : "border-[var(--mp-border)] hover:border-[var(--mp-ink)]"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={cfg.accept}
            className="hidden"
            onChange={onFileChange}
            data-testid={`direct-file-input-${mode}`}
          />
          {file ? (
            <div className="space-y-1">
              <cfg.fileIcon className="h-8 w-8 mx-auto" />
              <div className="font-display text-base tracking-tight truncate">
                {file.name}
              </div>
              <div className="text-[11px] text-[var(--mp-muted)]">
                {prettyBytes(file.size)}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Upload className="h-8 w-8 mx-auto text-[var(--mp-muted)]" />
              <div className="font-display text-base tracking-tight">
                {cfg.fileLabel}
              </div>
              <div className="text-[11px] text-[var(--mp-muted)]">
                {cfg.typeHint} · max {MAX_MB}MB
              </div>
            </div>
          )}
        </div>

        {uploading && (
          <div data-testid={`direct-progress-${mode}`} className="space-y-1">
            <div className="h-2 rounded-full bg-[var(--mp-dash-bg)] overflow-hidden">
              <div
                className="h-full bg-[var(--mp-ink)] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-[11px] text-[var(--mp-muted)]">
              Caricamento… {progress}%
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid={`direct-cancel-${mode}`}
          >
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
          <Button
            onClick={upload}
            disabled={!file || uploading}
            data-testid={`direct-upload-btn-${mode}`}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Upload className="h-4 w-4 mr-1" />
            {uploading ? `Upload ${progress}%` : cfg.submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
